import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Button, Paper, Stack, Link } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SEO from '../components/SEO';
import LegislatorMeetingForm from '../components/LegislatorMeetingForm';
import StateLookupDialog from '../components/StateLookupDialog';
import { generateWebPageSchema } from '../utils/schemaGenerators';
import { trackEvent } from '../components/GoogleAnalytics';
import HeroImage from '../assets/images/garza-paxton-photo-centered.png';

const LegislatorMeetings: React.FC = () => {
  const [stateLookupDialogOpen, setStateLookupDialogOpen] = useState(false);

  useEffect(() => {
    if (window.location.hash === '#meeting-request-form') {
      document.getElementById('meeting-request-form')?.focus();
    }
  }, []);

  const howItWorksSteps = [
    {
      title: 'Connect with your legislator',
      description: 'Contact your state legislator and ask about a meeting to discuss family court reform.',
    },
    {
      title: 'Request Robert’s participation',
      description: 'Share your legislator’s details, a proposed date, and the bills you’d like to discuss.',
    },
    {
      title: 'Coordinate with our team',
      description: 'Our team will follow up to coordinate scheduling and Robert’s participation.',
    },
  ];

  return (
    <Box
      component="main"
      sx={{
        '& .MuiButton-root': { minHeight: 44 },
        '& a:focus-visible, & button:focus-visible, & [tabindex="-1"]:focus-visible': {
          outline: '3px solid',
          outlineColor: 'text.primary',
          outlineOffset: 4,
        },
        '& .MuiButton-outlined, & .MuiLink-root, & a:not(.MuiButton-contained)': {
          color: (theme) => (theme.palette.mode === 'light' ? '#765600' : theme.palette.primary.main),
        },
      }}
    >
      <SEO
        title="RobertGarza.us Family Court Reform - Legislator Meetings"
        description="Request Robert Garza's participation in legislator meetings. Advocates can request expert support for comprehensive family court reform policy discussions."
        path="/legislator-meetings"
        schema={generateWebPageSchema({
          name: 'RobertGarza.us Family Court Reform - Legislator Meetings',
          description:
            "Request Robert Garza's participation in legislator meetings. Advocates can request expert support for comprehensive family court reform policy discussions.",
          url: '/legislator-meetings',
        })}
      />

      <Container maxWidth="lg" sx={{ pt: { xs: 4, md: 7 }, pb: { xs: 4, md: 6 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.15fr 1fr' },
            gap: { xs: 3, md: 7 },
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.12em' }}>
              Take action · Family court reform
            </Typography>
            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                lineHeight: 1.15,
                mt: 1,
                mb: 2,
                fontSize: { xs: '2.25rem', sm: '3rem', md: '3.5rem' },
              }}
            >
              Legislator meetings.
              <Box component="span" sx={{ display: 'block', fontWeight: 400 }}>
                Bring support to the table.
              </Box>
            </Typography>
            <Typography color="text.secondary" sx={{ maxWidth: '52ch', mb: 3, lineHeight: 1.75 }}>
              You make the connection. Robert Garza brings legislative experience and family court reform expertise to
              your conversation.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                size="large"
                href="#meeting-request-form"
                endIcon={<ArrowForwardIcon />}
                onClick={(event) => {
                  event.preventDefault();
                  trackEvent('cta_click', 'engagement', 'Request Robert for a Meeting - Legislator Meetings');
                  document.getElementById('meeting-request-form')?.scrollIntoView({ block: 'start' });
                  document
                    .querySelector<HTMLInputElement>('#meeting-request-form input[name="fullName"]')
                    ?.focus({ preventScroll: true });
                }}
              >
                Request Robert’s participation
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => {
                  trackEvent('cta_click', 'engagement', 'Legislator Lookup - Hero');
                  setStateLookupDialogOpen(true);
                }}
              >
                Find your legislator
              </Button>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              For advocates arranging a phone or video meeting with a local state legislator.
            </Typography>
          </Box>
          <Box component="figure" sx={{ m: 0 }}>
            <Box
              component="img"
              src={HeroImage}
              alt="Robert Garza with attendees at a legislative event"
              sx={{
                display: 'block',
                width: '100%',
                aspectRatio: { xs: '16 / 9', md: '4 / 3' },
                objectFit: 'cover',
                objectPosition: 'center 30%',
                borderRadius: 3,
              }}
            />
            <Typography component="figcaption" variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Robert Garza advocates for family court reform with lawmakers.
            </Typography>
          </Box>
        </Box>
      </Container>

      <Box
        component="section"
        aria-labelledby="how-it-works-title"
        sx={{ bgcolor: 'background.paper', borderBlock: 1, borderColor: 'divider', py: { xs: 4, md: 5 } }}
      >
        <Container maxWidth="lg">
          <Typography
            id="how-it-works-title"
            variant="h2"
            sx={{ fontWeight: 700, mb: 3, fontSize: { xs: '1.5rem', md: '2rem' } }}
          >
            From first contact to a meaningful conversation
          </Typography>
          <Box
            component="ol"
            sx={{
              m: 0,
              p: 0,
              listStyle: 'none',
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: { xs: 3, md: 4 },
            }}
          >
            {howItWorksSteps.map((step, index) => (
              <Box component="li" key={step.title} sx={{ display: 'flex', gap: 2 }}>
                <Box
                  aria-hidden="true"
                  sx={{
                    color: 'text.primary',
                    borderTop: 3,
                    borderColor: 'primary.main',
                    pt: 0.5,
                    fontWeight: 800,
                    fontSize: '1.25rem',
                  }}
                >
                  0{index + 1}
                </Box>
                <Box>
                  <Typography variant="h6" component="h3" sx={{ fontSize: '1.05rem', fontWeight: 700, mb: 1 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                    {step.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 7 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 2.2fr)' },
            gap: { xs: 3, md: 6 },
            alignItems: 'start',
          }}
        >
          <Box component="aside" aria-labelledby="before-you-start-title">
            <Typography variant="overline" sx={{ fontWeight: 700 }}>
              Your next step
            </Typography>
            <Typography
              id="before-you-start-title"
              variant="h2"
              sx={{ fontWeight: 700, mb: 2, fontSize: { xs: '1.5rem', md: '2rem' } }}
            >
              Let’s prepare for your meeting
            </Typography>
            <Typography color="text.secondary" sx={{ lineHeight: 1.75 }}>
              Have these details ready:
            </Typography>
            <Box component="ul" sx={{ pl: 2.5, color: 'text.secondary', '& li': { mb: 1, lineHeight: 1.75 } }}>
              <li>Your legislator’s name and role</li>
              <li>A proposed date and time</li>
              <li>A video link or dial-in number</li>
            </Box>
            <Link
              component="button"
              type="button"
              onClick={() => setStateLookupDialogOpen(true)}
              sx={{ textAlign: 'left', minHeight: 44, fontWeight: 600 }}
            >
              Need help finding your legislator?
            </Link>
            <Box sx={{ borderTop: 1, borderColor: 'divider', mt: 2, pt: 3 }}>
              <Typography component="h3" variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                What happens after you send?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                Our team will review your request and follow up to coordinate scheduling. Submitting a request does not
                confirm a meeting.
              </Typography>
            </Box>
          </Box>
          <Paper variant="outlined" sx={{ minWidth: 0, p: { xs: 2, sm: 3, lg: 4 }, borderRadius: 3 }}>
            <LegislatorMeetingForm id="meeting-request-form" />
          </Paper>
        </Box>
      </Container>
      <StateLookupDialog open={stateLookupDialogOpen} onClose={() => setStateLookupDialogOpen(false)} />
    </Box>
  );
};

export default LegislatorMeetings;
